import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';

const MOBILE_MODAL_MEDIA_QUERY = '(max-width: 1023px), (pointer: coarse)';
const MIN_VIEWPORT_HEIGHT_PX = 320;

type KeyboardSafeViewport = {
  height: number;
  offsetTop: number;
};

function getViewportSnapshot(): KeyboardSafeViewport {
  if (typeof window === 'undefined') {
    return {
      height: MIN_VIEWPORT_HEIGHT_PX,
      offsetTop: 0,
    };
  }

  const visualViewport = window.visualViewport;
  return {
    height: Math.max(Math.round(visualViewport?.height ?? window.innerHeight), MIN_VIEWPORT_HEIGHT_PX),
    offsetTop: Math.max(Math.round(visualViewport?.offsetTop ?? 0), 0),
  };
}

function getIsKeyboardSafeLayout(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return window.matchMedia(MOBILE_MODAL_MEDIA_QUERY).matches;
}

export type KeyboardSafeModalState = {
  dialogRef: RefObject<HTMLDivElement | null>;
  isKeyboardSafeLayout: boolean;
  dialogAutoFocus: boolean;
  viewportStyle: CSSProperties | undefined;
};

export function useKeyboardSafeModal(open: boolean): KeyboardSafeModalState {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previousFocusedElementRef = useRef<HTMLElement | null>(null);
  const [isKeyboardSafeLayout, setIsKeyboardSafeLayout] = useState(getIsKeyboardSafeLayout);
  const [viewport, setViewport] = useState<KeyboardSafeViewport>(getViewportSnapshot);

  useEffect(() => {
    if (!open) {
      return;
    }

    previousFocusedElementRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const mediaQueryList = typeof window.matchMedia === 'function' ? window.matchMedia(MOBILE_MODAL_MEDIA_QUERY) : null;
    const syncViewport = () => {
      setIsKeyboardSafeLayout(mediaQueryList?.matches ?? getIsKeyboardSafeLayout());
      setViewport(getViewportSnapshot());
    };

    const previousBodyOverflow = document.body.style.overflow;
    syncViewport();
    document.body.style.overflow = 'hidden';

    mediaQueryList?.addEventListener('change', syncViewport);
    window.addEventListener('resize', syncViewport);
    window.visualViewport?.addEventListener('resize', syncViewport);
    window.visualViewport?.addEventListener('scroll', syncViewport);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      mediaQueryList?.removeEventListener('change', syncViewport);
      window.removeEventListener('resize', syncViewport);
      window.visualViewport?.removeEventListener('resize', syncViewport);
      window.visualViewport?.removeEventListener('scroll', syncViewport);

      const previousFocusedElement = previousFocusedElementRef.current;
      previousFocusedElementRef.current = null;
      if (previousFocusedElement && document.contains(previousFocusedElement)) {
        requestAnimationFrame(() => {
          previousFocusedElement.focus({ preventScroll: true });
        });
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open || !isKeyboardSafeLayout) {
      return;
    }

    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    const keepFocusedFieldVisible = () => {
      const activeElement = document.activeElement;
      if (!(activeElement instanceof HTMLElement) || !dialog.contains(activeElement)) {
        return;
      }

      if (typeof activeElement.scrollIntoView === 'function') {
        activeElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    };

    requestAnimationFrame(() => {
      if (document.activeElement === document.body) {
        dialog.focus({ preventScroll: true });
      }
      keepFocusedFieldVisible();
    });

    const handleFocusIn = () => {
      requestAnimationFrame(keepFocusedFieldVisible);
    };

    window.addEventListener('focusin', handleFocusIn);
    window.visualViewport?.addEventListener('resize', handleFocusIn);
    window.visualViewport?.addEventListener('scroll', handleFocusIn);

    return () => {
      window.removeEventListener('focusin', handleFocusIn);
      window.visualViewport?.removeEventListener('resize', handleFocusIn);
      window.visualViewport?.removeEventListener('scroll', handleFocusIn);
    };
  }, [isKeyboardSafeLayout, open]);

  return {
    dialogRef,
    isKeyboardSafeLayout,
    dialogAutoFocus: !isKeyboardSafeLayout,
    viewportStyle: open && isKeyboardSafeLayout
      ? {
          top: `${viewport.offsetTop}px`,
          bottom: 'auto',
          height: `${viewport.height}px`,
        }
      : undefined,
  };
}
